import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';

function Breadcrumb({
    className,
    ...props
}: React.ComponentProps<'nav'>) {
    return (
        <nav
            aria-label="breadcrumb"
            data-slot="breadcrumb"
            className={className}
            {...props}
        />
    );
}

function BreadcrumbList({
    className,
    ...props
}: React.ComponentProps<'ol'>) {
    return (
        <ol
            data-slot="breadcrumb-list"
            className={cn(
                'flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground',
                className,
            )}
            {...props}
        />
    );
}

function BreadcrumbItem({
    className,
    ...props
}: React.ComponentProps<'li'>) {
    return (
        <li
            data-slot="breadcrumb-item"
            className={cn('inline-flex items-center gap-1.5', className)}
            {...props}
        />
    );
}

function BreadcrumbLink({
    className,
    children,
    asChild = false,
    ...props
}: React.ComponentProps<'a'> & { asChild?: boolean }) {
    const Comp = asChild ? Slot : 'a';
    return (
        <Comp
            data-slot="breadcrumb-link"
            className={cn(
                'transition-colors hover:text-foreground',
                className,
            )}
            {...props}
        >
            {children}
        </Comp>
    );
}

function BreadcrumbPage({
    className,
    ...props
}: React.ComponentProps<'span'>) {
    return (
        <span
            data-slot="breadcrumb-page"
            role="link"
            aria-disabled="true"
            aria-current="page"
            className={cn('font-medium text-foreground', className)}
            {...props}
        />
    );
}

function BreadcrumbSeparator({
    className,
    children,
    ...props
}: React.ComponentProps<'li'>) {
    return (
        <li
            data-slot="breadcrumb-separator"
            role="presentation"
            aria-hidden="true"
            className={cn('text-muted-foreground/60', className)}
            {...props}
        >
            {children ?? <ChevronRight className="h-3.5 w-3.5" />}
        </li>
    );
}

export {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
};
